import React, { forwardRef } from 'react';
import { cn } from '../../lib/utils';
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'ios' | 'tiktok';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  isLoading?: boolean;
}
const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant = 'default',
  size = 'default',
  isLoading = false,
  children,
  disabled,
  ...props
}, ref) => {
  const isDisabled = disabled || isLoading;
  return <button className={cn(
  // Base styles
  'inline-flex items-center justify-center font-medium transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', 'disabled:pointer-events-none disabled:opacity-50', 'active:scale-[0.98] active:transition-transform', 'transition-all duration-200',
  // Variants
  {
    // Default - TikTok-style pink button
    'bg-primary text-primary-foreground hover:bg-primary/90 rounded-full': variant === 'default',
    // iOS - Filled button with rounded corners
    'bg-primary text-primary-foreground hover:bg-primary/90 rounded-full': variant === 'ios',
    // TikTok - Gradient button with rounded corners
    'tiktok-gradient text-white rounded-full hover:brightness-95': variant === 'tiktok',
    // Destructive - Red button for dangerous actions
    'bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full': variant === 'destructive',
    // Outline - Subtle bordered button
    'border border-input bg-background hover:bg-secondary/50 hover:border-input/80 rounded-full': variant === 'outline',
    // Secondary - Dark gray background
    'bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full': variant === 'secondary',
    // Ghost - Text-like button with hover state
    'hover:bg-accent hover:text-accent-foreground rounded-full': variant === 'ghost',
    // Link - Appears as a hyperlink
    'text-primary underline-offset-4 hover:underline p-0 h-auto': variant === 'link'
  },
  // Sizes
  {
    'h-11 px-7 py-2 text-sm': size === 'default',
    'h-9 px-5 text-xs': size === 'sm',
    'h-12 px-8 text-base': size === 'lg',
    'h-11 w-11 p-0': size === 'icon'
  }, className)} ref={ref} disabled={isDisabled} {...props} data-id="element-1548">
        {isLoading ? <div className="flex items-center" data-id="element-1549">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1550">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1551"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1552"></path>
            </svg>
            {children}
          </div> : children}
      </button>;
});
Button.displayName = 'Button';
export { Button };